import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, UserPlus, Trash2, FileSearch, Users } from "lucide-react";
import ContextualUploader from "@/components/common/ContextualUploader";

const RELATIONSHIPS = ["Spouse", "Father", "Mother", "Son", "Daughter", "Brother", "Sister", "Other"];
const EMPLOYER_TYPES = ["Government", "PSU", "MNC", "Private", "Self-Employed"];

function emptyCoApp() {
  return {
    first_name: "", middle_name: "", last_name: "",
    relationship: "Spouse",
    pan: "", aadhaar_last4: "", mobile: "", email: "", dob: "", address: "",
    occupation: "", employer_name: "", employer_type: "Private",
    net_monthly_income: 0, existing_emi: 0,
  };
}

function Field({ label, children, className = "" }) {
  return (
    <div className={`space-y-1 ${className}`}>
      <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function CoApplicantCard({ app, co, onUpdated, onRemoved }) {
  const [busy, setBusy] = useState(false);

  const pull = async () => {
    if (!co.pan) return toast.error("PAN is required for bureau pull");
    setBusy(true);
    try {
      const r = await api.post(`/applications/${app.id}/co-applicants/${co.id}/bureau-pull`);
      toast.success(`Bureau pull complete · CIBIL ${r.data.co_applicant?.cibil_score || "—"}`);
      onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bureau pull failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove co-applicant ${co.first_name} ${co.last_name}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/applications/${app.id}/co-applicants/${co.id}`);
      toast.success("Co-applicant removed");
      onRemoved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const fullName = [co.first_name, co.middle_name, co.last_name].filter(Boolean).join(" ") || "—";
  const bureauReady = co.cibil_score || co.experian_score || co.equifax_score || co.crif_score;

  return (
    <Card className="p-4 space-y-3 border-border" data-testid={`co-applicant-card-${co.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-semibold heading text-sm">{fullName}</h4>
            <Badge variant="outline" className="text-[10px]">{co.relationship || "Co-applicant"}</Badge>
            {co.employer_type && <Badge variant="secondary" className="text-[10px]">{co.employer_type}</Badge>}
          </div>
          <p className="text-[11px] text-muted-foreground mt-1 mono">
            PAN: {co.pan || "—"} · Mobile: {co.mobile || "—"} · Income: ₹ {(co.net_monthly_income || 0).toLocaleString("en-IN")}/mo · EMI: ₹ {(co.existing_emi || 0).toLocaleString("en-IN")}/mo
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button size="sm" variant="outline" onClick={pull} disabled={busy} data-testid={`co-bureau-pull-${co.id}`}>
            <FileSearch className="h-3.5 w-3.5 mr-1" /> Pull Bureau
          </Button>
          <Button size="sm" variant="ghost" onClick={remove} disabled={busy} className="text-destructive hover:text-destructive" data-testid={`co-remove-${co.id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      {bureauReady && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-2 border-t border-border">
          <ScoreCell label="CIBIL" v={co.cibil_score} />
          <ScoreCell label="Experian" v={co.experian_score} />
          <ScoreCell label="Equifax" v={co.equifax_score} />
          <ScoreCell label="CRIF" v={co.crif_score} />
        </div>
      )}
      <div className="pt-2 border-t border-border space-y-1">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Co-applicant documents</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <ContextualUploader applicationId={app.id} category="CO_APPLICANT_KYC" title={`KYC — ${fullName}`} multiple tag={co.id} compact />
          <ContextualUploader applicationId={app.id} category="CO_APPLICANT_INCOME" title={`Income proof — ${fullName}`} multiple tag={co.id} compact />
        </div>
      </div>
    </Card>
  );
}

function ScoreCell({ label, v }) {
  if (!v) return (
    <div className="rounded-md border border-border bg-muted/30 p-2 text-center">
      <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
      <div className="text-xs text-muted-foreground mt-0.5">—</div>
    </div>
  );
  const cls = v >= 750 ? "text-success border-success/30" : v >= 700 ? "text-accent border-accent/30" : "text-warning border-warning/30";
  return (
    <div className={`rounded-md border bg-card p-2 text-center ${cls}`}>
      <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
      <div className="text-base font-semibold mt-0.5 num-right text-center">{v}</div>
    </div>
  );
}

export default function CoApplicantsSection({ app, coApplicants = [], reload }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyCoApp());
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canAdd = coApplicants.length < 3;

  const add = async () => {
    if (!form.first_name || !form.last_name) return toast.error("First & last name are required");
    setBusy(true);
    try {
      const payload = {
        ...form,
        net_monthly_income: parseFloat(form.net_monthly_income) || 0,
        existing_emi: parseFloat(form.existing_emi) || 0,
      };
      await api.post(`/applications/${app.id}/co-applicants`, payload);
      toast.success("Co-applicant added");
      setForm(emptyCoApp());
      setShowForm(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add co-applicant");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-base font-semibold heading flex items-center gap-2">
            <Users className="h-4 w-4 text-accent" /> Co-Applicants ({coApplicants.length}/3)
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Joint applicants whose income, EMIs and bureau scores are factored into CRA scoring.
          </p>
        </div>
        {canAdd && !showForm && (
          <Button onClick={() => setShowForm(true)} size="sm" data-testid="co-add-button">
            <UserPlus className="h-3.5 w-3.5 mr-1" /> Add Co-Applicant
          </Button>
        )}
      </div>

      {coApplicants.length === 0 && !showForm && (
        <Card className="p-8 text-center border-dashed">
          <Users className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No co-applicants added yet.</p>
          <p className="text-[11px] text-muted-foreground mt-1">Add up to 3 co-applicants for joint loan applications.</p>
        </Card>
      )}

      {coApplicants.map((co) => (
        <CoApplicantCard key={co.id} app={app} co={co} onUpdated={reload} onRemoved={reload} />
      ))}

      {showForm && (
        <Card className="p-4 space-y-3 border-accent/30 bg-accent/5">
          <div className="flex items-center gap-2"><Plus className="h-4 w-4 text-accent" /><h4 className="text-sm font-semibold heading">New Co-Applicant</h4></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="First Name *"><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="co-first-name" /></Field>
            <Field label="Middle Name"><Input value={form.middle_name} onChange={(e) => set("middle_name", e.target.value)} /></Field>
            <Field label="Last Name *"><Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="co-last-name" /></Field>
            <Field label="Relationship">
              <Select value={form.relationship} onValueChange={(v) => set("relationship", v)}>
                <SelectTrigger data-testid="co-relationship"><SelectValue /></SelectTrigger>
                <SelectContent>{RELATIONSHIPS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Mobile"><Input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} /></Field>
            <Field label="Email"><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
            <Field label="PAN"><Input value={form.pan} onChange={(e) => set("pan", e.target.value.toUpperCase())} placeholder="ABCDE1234F" data-testid="co-pan" /></Field>
            <Field label="Aadhaar (Last 4)"><Input value={form.aadhaar_last4} onChange={(e) => set("aadhaar_last4", e.target.value)} maxLength={4} /></Field>
            <Field label="DOB"><Input type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)} /></Field>
            <Field label="Employer Name"><Input value={form.employer_name} onChange={(e) => set("employer_name", e.target.value)} /></Field>
            <Field label="Employer Type">
              <Select value={form.employer_type} onValueChange={(v) => set("employer_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{EMPLOYER_TYPES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Occupation"><Input value={form.occupation} onChange={(e) => set("occupation", e.target.value)} /></Field>
            <Field label="Net Monthly Income (₹)"><Input type="number" value={form.net_monthly_income} onChange={(e) => set("net_monthly_income", e.target.value)} /></Field>
            <Field label="Existing EMI (₹)"><Input type="number" value={form.existing_emi} onChange={(e) => set("existing_emi", e.target.value)} /></Field>
            <Field label="Address" className="md:col-span-3"><Input value={form.address} onChange={(e) => set("address", e.target.value)} /></Field>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <Button variant="ghost" onClick={() => { setShowForm(false); setForm(emptyCoApp()); }} disabled={busy}>Cancel</Button>
            <Button onClick={add} disabled={busy} data-testid="co-save-button">{busy ? "Saving…" : "Add Co-Applicant"}</Button>
          </div>
        </Card>
      )}
    </div>
  );
}
